import EmptyAssetCard from "common/features/asset/EmptyAssetCard";
import styles from "./Material.module.scss";
import MaterialMetaData from "./MaterialMetaData";
import { useCallback, useEffect, useRef, useState } from "react";
import MaterialPanel from "./MaterialPanel";
import useMaterialActions from "./useMaterialActions";
import MaterialActions from "./MaterialActions";
import AccessCodeDialog from "./AccessCodeDialog";
import ConfirmOptOutItemDialog from "./ConfirmOptOutItemDialog";
import ProgramSwitcher from "./ProgramSwitcher";
import {
  CourseMaterial,
  TermLite,
  useCoursesLoaderData,
  useProgramDetails,
} from "./coursesLoader";
import { Location, useLocation, useNavigate } from "react-router-dom";
import PrintStatusTag from "./PrintStatusTag";
import clsx from "clsx";
import ConfirmOptInPrintDialog from "./ConfirmOptInPrintDialog";
import { ConfirmOptInEvent, PrintOptInEvent } from "./PrintStatusChangeToast";
import LmsLinkDialog from "./LmsLinkDialog";
import RequiredCodeTag from "../common/RequiredCodeTag.tsx";
import useVirtualStore from "../../common/virtual-store/useVirtualStore.tsx";
import { getImageSource } from "./materialUtils.ts";
import useLaunchBook from "./useLaunchBook.tsx";
import useLaunchPiCourseware from "./useLaunchPiCourseware.tsx";
import StoreStockStatusTag from "./StoreStockStatusTag.tsx";
import LibraryAccessCodeDialog from "./LibraryAccessCodeDialog.tsx";
import { focusWithSetTabIndex } from "../../../../common/utils/focus.ts";
import { areCourseIdsTheSame, CourseDeepLinkState } from "../coursesUtils.ts";
import MiniPdpContextProvider from "../../common/product/mini-pdp/MiniPdpContextProvider.tsx";
import { getMaterialKind } from "./common/materialStoreProductUtils.ts";
import ShippingStatusTag from "./ShippingStatusTag.tsx";
import useOrderInfo from "./useOrderInfo.tsx";
import MaterialTopBanner from "./MaterialTopBanner.tsx";
import LicenseDuration from "./common/LicenseDuration.tsx";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton.tsx";
import MaterialAccessBlock from "./MaterialAccessBlock.tsx";
import { useFocusPreservation } from "common/core-ui/useFocusPreservation.ts";
import MaterialStartDate from "./MaterialStartDate.tsx";
import Instructions from "./Instructions.tsx";

function normalizeMaterialId(id: string) {
  return id.toLowerCase().replace("-", "");
}

const GetStatusBlock = ({
  courseMaterial,
  className,
  currentTerm,
}: {
  courseMaterial: CourseMaterial;
  className: string;
  currentTerm: TermLite | undefined;
}) => {
  const { orderStatus } = useOrderInfo(courseMaterial);
  const pickupDate = currentTerm?.printInfo?.pickupDate;
  return (
    <div className={clsx(styles.statusContainer, className)}>
      {courseMaterial.requiredCode && (
        <div
          className={styles.requiredCodeTagContainer}
          data-testid="requiredCode"
        >
          <RequiredCodeTag code={courseMaterial.requiredCode} />
        </div>
      )}
      <div className={styles.statusTagContainer}>
        <ShippingStatusTag orderStatus={orderStatus} />
        <PrintStatusTag
          printStatus={courseMaterial.printStatus}
          pickupDate={pickupDate}
        />
        <StoreStockStatusTag stockItem={courseMaterial} />
      </div>
    </div>
  );
};

function MaterialInner({ courseMaterial }: { courseMaterial: CourseMaterial }) {
  const { currentTerm } = useCoursesLoaderData();
  const {
    vbid,
    isbn,
    storeIdentifier,
    title,
    displayNotes,
    courseId,
    attachments,
  } = courseMaterial;
  const [openPanelInfo, setOpenPanelInfo] = useState<boolean>(false);
  const [isProgramSwitcherOpen, setIsProgramSwitcherOpen] =
    useState<boolean>(false);
  const [openConfirmOptOutDialog, setOpenConfirmOptOutDialog] =
    useState<boolean>(false);
  const [openConfirmOptInPrintDialog, setOpenConfirmOptInPrintDialog] =
    useState<boolean>(false);
  const [coursesAccessCodeUrl, setCoursesAccessCodeUrl] = useState("");
  const [lmsLinkDialogUrl, setLmsLinkDialogUrl] = useState("");
  const [libraryAccessCodeUrl, setLibraryAccessCodeUrl] = useState("");
  const { launchBook } = useLaunchBook();
  const { launchPiCourseware } = useLaunchPiCourseware(
    (lmsLink) => lmsLink && setLmsLinkDialogUrl(lmsLink)
  );
  const navigate = useNavigate();
  const { isVirtualStore } = useVirtualStore();
  const { scope } = useProgramDetails();
  const selectProgram =
    scope === "flexible" ? () => setIsProgramSwitcherOpen(true) : undefined;
  const cardRef = useRef<HTMLLIElement>(null);
  const { state: navState } =
    useLocation() as Location<CourseDeepLinkState | null>;

  const methods = {
    comparePurchaseOptions: () =>
      navigate(
        `/my-courses/purchase-options/${currentTerm!.id}/${courseId}/${vbid}`
      ),
    confirmOptOutDialog: () => setOpenConfirmOptOutDialog(true),
    confirmOptInPrintDialog: () => setOpenConfirmOptInPrintDialog(true),
    viewDetails: () => setOpenPanelInfo(true),
    launchBook: (path: string) => launchBook(path),
    launchPiCourseware: (path: string) => launchPiCourseware(path),
    coursesAccessCodeDialog: (url: string) => setCoursesAccessCodeUrl(url),
    lmsLinkDialog: (url: string) => setLmsLinkDialogUrl(url),
    libraryAccessCodeDialog: (url: string) => setLibraryAccessCodeUrl(url),
  };
  const actions = useMaterialActions({
    courseMaterial,
    methods,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  useFocusPreservation(containerRef.current);

  const viewDetailsActions = actions.filter(
    (action) =>
      action.key === "view_details" || action.key === "view_main_pdp_details"
  );
  const viewDetails = viewDetailsActions[0];

  const primaryAction =
    actions.find((a) => a.key === "open_book") ??
    actions.find((a) => a.key === "view_details") ??
    actions.find((a) => a.key === "view_main_pdp_details");

  const optOutAction = actions.find((action) => action.type === "opt_out");
  const optInAction = actions.find((action) => action.type === "opt_in");
  const showActions = actions.filter(
    (action) =>
      action.key !== "view_purchase_options" &&
      action.key !== "view_details" &&
      action.key !== "view_main_pdp_details"
  );

  const handlePrintOptIn = useCallback(
    (event: CustomEvent<ConfirmOptInEvent>) => {
      if (event?.detail?.courseMaterialId === courseMaterial.id) {
        const action = actions.find(
          (action) => action.type === "confirm_print_pickup"
        );
        if (action?.onClick) {
          void action.onClick();
        }
      }
    },
    [actions, courseMaterial.id]
  );

  useEffect(() => {
    window.addEventListener(PrintOptInEvent, handlePrintOptIn);

    return () => {
      window.removeEventListener(PrintOptInEvent, handlePrintOptIn);
    };
  }, [handlePrintOptIn]);

  useEffect(() => {
    const materialId = navState?.materialId;
    const courseId = navState?.courseId;

    if (
      cardRef.current &&
      courseId &&
      materialId &&
      areCourseIdsTheSame(courseId, courseMaterial.courseId) &&
      [
        courseMaterial.isbn,
        courseMaterial.vbid,
        ...(courseMaterial.product?.map((product) => product.canonicalIsbn) ??
          []),
      ]
        .map((id) => normalizeMaterialId(id))
        .includes(normalizeMaterialId(materialId))
    ) {
      // If we remove this we will want to remove the default focus suppression in useRouteMainFocus
      focusWithSetTabIndex(cardRef.current);
      cardRef.current.scrollIntoView();
    }
  }, [navState, courseMaterial]);

  return (
    <li className={styles.wholeCard} ref={cardRef}>
      <MaterialTopBanner material={courseMaterial} />
      <div className={styles.cardContainer}>
        <GetStatusBlock
          courseMaterial={courseMaterial}
          className={styles.statusContainerSmall}
          currentTerm={currentTerm}
        />
        <div className={styles.contentArea}>
          <EmptyAssetCard
            tagName="div"
            vbid={isVirtualStore ? storeIdentifier ?? isbn : vbid}
            type="horizontal"
            imageSource={getImageSource(courseMaterial)}
            contentAreaClassName={styles.contentContainer}
            className={styles.baseCard}
            primaryAction={
              primaryAction
                ? {
                    onClick: primaryAction.onClick
                      ? () => void primaryAction.onClick?.()
                      : undefined,
                    href: primaryAction.href,
                    to: primaryAction.to,
                  }
                : undefined
            }
          >
            <div className={styles.metaDataContainer}>
              <MaterialMetaData
                showAccessDuration={false}
                courseMaterial={courseMaterial}
                titleTag={"h3"}
                titleAction={primaryAction}
                showTags
              />
              {viewDetails && (
                <div key={viewDetails.label}>
                  <BaseLinkOrButton
                    aria-label={viewDetails.ariaLabel}
                    href={viewDetails.href}
                    to={viewDetails.to}
                    onClick={() => void viewDetails?.onClick?.()}
                    className={clsx(
                      "vst-link",
                      "vst-link-size-small",
                      styles.viewDetailsActions
                    )}
                  >
                    {viewDetails.label}
                  </BaseLinkOrButton>
                </div>
              )}
            </div>
          </EmptyAssetCard>
        </div>
        <div className={styles.verticalDivider} aria-hidden="true" />
        <div className={styles.accessContainer}>
          <GetStatusBlock
            courseMaterial={courseMaterial}
            className={styles.statusContainerLarge}
            currentTerm={currentTerm}
          />
          <div ref={containerRef}>
            <MaterialAccessBlock
              material={courseMaterial}
              optOutAction={optOutAction}
              optInAction={optInAction}
            />
            <div className={styles.actionsContainer}>
              <MaterialActions
                material={courseMaterial}
                actions={showActions}
              />
            </div>
            <LicenseDuration
              className={styles.licenseDuration}
              courseMaterial={courseMaterial}
            />
            <MaterialStartDate
              className={styles.startDate}
              material={courseMaterial}
            />
          </div>
        </div>
      </div>
      <Instructions attachments={attachments} displayNotes={displayNotes} />
      {openPanelInfo && (
        <MaterialPanel
          courseMaterial={courseMaterial}
          actions={actions}
          setOpenPanelInfo={() => setOpenPanelInfo(false)}
        />
      )}
      {coursesAccessCodeUrl && (
        <AccessCodeDialog
          title={title}
          displayNotes={displayNotes}
          vbid={vbid}
          onRequestClose={() => setCoursesAccessCodeUrl("")}
          fetchUrl={coursesAccessCodeUrl}
        />
      )}
      {lmsLinkDialogUrl && (
        <LmsLinkDialog
          onRequestClose={() => setLmsLinkDialogUrl("")}
          materialFormat={courseMaterial.materialFormat}
          href={lmsLinkDialogUrl}
        />
      )}
      {openConfirmOptOutDialog && (
        <ConfirmOptOutItemDialog
          courseMaterial={courseMaterial}
          selectProgram={selectProgram}
          setOpenConfirmOptOutDialog={() => setOpenConfirmOptOutDialog(false)}
        />
      )}
      {openConfirmOptInPrintDialog && (
        <ConfirmOptInPrintDialog
          courseMaterial={courseMaterial}
          onRequestClose={() => setOpenConfirmOptInPrintDialog(false)}
        />
      )}
      {scope === "flexible" && isProgramSwitcherOpen && (
        <ProgramSwitcher
          onRequestClose={() => setIsProgramSwitcherOpen(false)}
        />
      )}
      {libraryAccessCodeUrl && (
        <LibraryAccessCodeDialog
          courseMaterial={courseMaterial}
          onRequestClose={() => setLibraryAccessCodeUrl("")}
          linkedUrl={libraryAccessCodeUrl}
        />
      )}
    </li>
  );
}

export default function Material({
  courseMaterial,
}: {
  courseMaterial: CourseMaterial;
}) {
  return (
    <MiniPdpContextProvider
      identifier={courseMaterial.storeIdentifier}
      product={courseMaterial.product}
      kind={getMaterialKind(courseMaterial)}
      sidePanelType={"default"}
    >
      <MaterialInner courseMaterial={courseMaterial} />
    </MiniPdpContextProvider>
  );
}
